import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  App,
  Button,
  Card,
  Col,
  Dropdown,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography
} from 'antd';
import type { MenuProps, TableColumnsType } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  FilterOutlined,
  MoreOutlined,
  PlusOutlined,
  SearchOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { authService } from '../services/auth';
import CustomerContactCell from '../components/customers/CustomerContactCell';
import CustomerForm from '../components/customers/CustomerForm';
import { applyCustomerSaveError } from '../components/customers/customerFormErrors';
import {
  getCustomerContactsFieldValue,
  getCustomerContactsSearchText,
  normalizeCustomerContacts
} from '../utils/customerContacts';
import { matchesCustomerSearch } from '../modules/reference-data/customerSearch';
import {
  canCreateCustomers as canCreateCustomersForUser,
  canDeleteCustomers as canDeleteCustomersForUser,
  canEditCustomers as canEditCustomersForUser,
  isCustomerTesterMode,
} from '../modules/customers/customerAccessPolicy';
import { createCustomer } from '../modules/customers/api/customersApi';
import type { Customer, CustomerSubmitValues } from '../modules/customers/types';
import { getVatState, getVatValidationTranslation } from '../utils/customerVatStatus';
import './Customers.css';

const { Text } = Typography;

type VatFilter = 'all' | 'valid' | 'invalid' | 'unchecked';
type SortField = 'name' | 'phone' | 'taxId' | 'regon' | 'address' | 'vatStatus';
type SortDirection = 'asc' | 'desc';
type CustomerView = 'active' | 'archived';

interface CustomerFilters {
  search: string;
  name: string;
  phone: string;
  taxId: string;
  regon: string;
  address: string;
  vatStatus: VatFilter;
}

const EMPTY_VALUE = '—';

const DEFAULT_FILTERS: CustomerFilters = {
  search: '',
  name: '',
  phone: '',
  taxId: '',
  regon: '',
  address: '',
  vatStatus: 'all'
};

const normalizeText = (value?: string | null) => (value || '').trim().toLowerCase();

const compareText = (left?: string | null, right?: string | null) =>
  normalizeText(left).localeCompare(normalizeText(right), 'pl', {
    numeric: true,
    sensitivity: 'base'
  });

const formatDateTime = (value?: string) => {
  if (!value) return EMPTY_VALUE;
  return new Date(value).toLocaleString('pl-PL');
};

const escapeCsvCell = (value?: string | null) => `"${String(value || '').replace(/"/g, '""')}"`;

const getCustomerContacts = (customer: Customer) =>
  normalizeCustomerContacts(customer.contactPeople, customer.contactPerson, customer.phone);

const getCustomerParentName = (
  customer: Pick<Customer, 'parentCustomerId'>,
  customerNamesById: Map<string, string>
) => (customer.parentCustomerId ? customerNamesById.get(customer.parentCustomerId) || null : null);

const Customers: React.FC = () => {
  const { t } = useTranslation(['customers', 'common']);
  const { modal, message } = App.useApp();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [filters, setFilters] = useState<CustomerFilters>(DEFAULT_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [customerView, setCustomerView] = useState<CustomerView>('active');
  const [customerPendingDelete, setCustomerPendingDelete] = useState<Customer | null>(null);
  const [deletingCustomerId, setDeletingCustomerId] = useState<string | null>(null);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<string[]>([]);
  const [form] = Form.useForm();

  const currentUser = authService.getCurrentUser();
  const isTester = isCustomerTesterMode(currentUser);
  const canEditCustomers = canEditCustomersForUser(currentUser);
  const canCreateCustomers = canCreateCustomersForUser(currentUser);
  const canDeleteCustomers = canDeleteCustomersForUser(currentUser);
  const canViewCustomerPayments = authService.canViewCustomerPayments();

  useEffect(() => {
    if (!isTester) fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/customers?status=all');
      setCustomers(response.data);
    } catch (error) {
      message.error(t('customers:messages.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const customerNamesById = useMemo(
    () => new Map(customers.map((customer) => [customer.id, customer.name] as const)),
    [customers]
  );

  const openCreateModal = () => {
    setModalVisible(true);
  };

  const closeCustomerModal = () => {
    setModalVisible(false);
  };

  const handleCreate = async (values: CustomerSubmitValues) => {
    setSubmitting(true);
    form.setFields([
      { name: 'name', errors: [] },
      { name: 'contactPeople', errors: [] },
      { name: 'taxId', errors: [] },
      { name: 'regon', errors: [] },
      { name: 'parentCustomerId', errors: [] }
    ]);

    try {
      const created = await createCustomer(values);
      message.success(t('customers:messages.createSuccess'));
      closeCustomerModal();
      // Jump straight into the new customer's card so the operator can keep working.
      if (created?.id) {
        navigate(`/app/customers/${created.id}`);
      } else {
        fetchCustomers();
      }
    } catch (error: any) {
      applyCustomerSaveError(error, form, t, message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCustomer = async (customerId: string) => {
    setDeletingCustomerId(customerId);

    try {
      const response = await api.delete(`/customers/${customerId}`);
      if (response.data?.archived) {
        message.success(t('customers:customerArchived', { count: response.data.orderCount }));
      } else {
        message.success(t('customers:customerDeleted'));
      }
      setCustomerPendingDelete(null);
      fetchCustomers();
    } catch (error: any) {
      if (
        error?.response?.data?.message === 'Customer with active branches cannot be deleted' ||
        error?.response?.data?.message === 'Customer with branches cannot be deleted'
      ) {
        message.error(t('customers:messages.deleteBlockedByBranches'));
      } else {
        message.error(t('customers:messages.deleteError'));
      }
    } finally {
      setDeletingCustomerId(null);
    }
  };

  const handleExportCsv = () => {
    if (filteredAndSortedCustomers.length === 0) {
      message.warning(t('customers:messages.exportEmpty'));
      return;
    }

    const rows = [
      [
        'customerId',
        t('customers:table.name'),
        'isBranch',
        'parentCustomerId',
        'parentCustomerName',
        t('customers:preview.contactPerson'),
        t('customers:preview.contactEmail'),
        t('customers:table.phone'),
        t('customers:table.taxId'),
        t('customers:preview.vatStatus'),
        t('customers:table.regon'),
        t('customers:table.address')
      ],
      ...filteredAndSortedCustomers.map((customer) => [
        customer.id,
        customer.name || '',
        customer.isBranch ? 'true' : 'false',
        customer.parentCustomerId || '',
        getCustomerParentName(customer, customerNamesById) || '',
        getCustomerContactsFieldValue(customer.contactPeople, 'name', customer.contactPerson, customer.phone),
        customer.email || '',
        getCustomerContactsFieldValue(customer.contactPeople, 'phone', customer.contactPerson, customer.phone),
        customer.taxId || '',
        getVatState(customer) === 'unchecked'
          ? t('customers:validation.notChecked')
          : getVatState(customer) === 'valid'
            ? t('customers:table.valid')
            : t('customers:table.invalid'),
        customer.regon || '',
        customer.address || ''
      ])
    ];

    const csvContent = rows
      .map((row) => row.map((cell) => escapeCsvCell(cell)).join(';'))
      .join('\n');

    const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    const today = new Date().toISOString().slice(0, 10);

    link.href = url;
    link.setAttribute('download', `customers-${today}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    message.success(t('customers:messages.exportSuccess', { count: filteredAndSortedCustomers.length }));
  };

  const activeFilterCount = Object.entries(filters).filter(([key, value]) => {
    if (key === 'vatStatus') {
      return value !== 'all';
    }
    return Boolean(String(value).trim());
  }).length;

  const activeCustomersCount = useMemo(
    () => customers.filter((customer) => !customer.deletedAt).length,
    [customers]
  );

  const archivedCustomersCount = useMemo(
    () => customers.filter((customer) => Boolean(customer.deletedAt)).length,
    [customers]
  );

  const scopedCustomers = useMemo(
    () => customers.filter((customer) => (customerView === 'active' ? !customer.deletedAt : Boolean(customer.deletedAt))),
    [customerView, customers]
  );

  const filteredAndSortedCustomers = useMemo(() => {
    const filtered = scopedCustomers.filter((customer) => {
      if (
        filters.search &&
        !matchesCustomerSearch(
          {
            ...customer,
            parentName: getCustomerParentName(customer, customerNamesById),
            contactPerson:
              getCustomerContactsSearchText(
                customer.contactPeople,
                customer.contactPerson,
                customer.phone
              ) || customer.contactPerson,
          },
          filters.search
        )
      ) {
        return false;
      }

      if (filters.name && !normalizeText(customer.name).includes(normalizeText(filters.name))) {
        return false;
      }

      if (
        filters.phone &&
        !normalizeText(
          getCustomerContactsFieldValue(
            customer.contactPeople,
            'phone',
            customer.contactPerson,
            customer.phone
          )
        ).includes(normalizeText(filters.phone))
      ) {
        return false;
      }

      if (filters.taxId && !normalizeText(customer.taxId).includes(normalizeText(filters.taxId))) {
        return false;
      }

      if (filters.regon && !normalizeText(customer.regon).includes(normalizeText(filters.regon))) {
        return false;
      }

      if (filters.address && !normalizeText(customer.address).includes(normalizeText(filters.address))) {
        return false;
      }

      const vatState = getVatState(customer);

      if (filters.vatStatus === 'valid' && vatState !== 'valid') {
        return false;
      }

      if (filters.vatStatus === 'invalid' && vatState !== 'invalid') {
        return false;
      }

      if (filters.vatStatus === 'unchecked' && vatState !== 'unchecked') {
        return false;
      }

      return true;
    });

    const getVatSortValue = (customer: Customer) => {
      switch (getVatState(customer)) {
        case 'valid':
          return 2;
        case 'invalid':
          return 1;
        default:
          return 0;
      }
    };

    filtered.sort((left, right) => {
      const baseResult = (() => {
        switch (sortField) {
          case 'phone':
            return compareText(
              getCustomerContactsFieldValue(left.contactPeople, 'phone', left.contactPerson, left.phone),
              getCustomerContactsFieldValue(right.contactPeople, 'phone', right.contactPerson, right.phone)
            );
          case 'taxId':
            return compareText(left.taxId, right.taxId);
          case 'regon':
            return compareText(left.regon, right.regon);
          case 'address':
            return compareText(left.address, right.address);
          case 'vatStatus':
            return getVatSortValue(left) - getVatSortValue(right);
          case 'name':
          default:
            return compareText(left.name, right.name);
        }
      })();

      if (baseResult !== 0) {
        return sortDirection === 'asc' ? baseResult : -baseResult;
      }

      return compareText(left.name, right.name);
    });

    return filtered;
  }, [customerNamesById, filters, scopedCustomers, sortDirection, sortField]);

  const renderVatStatusIcon = (customer: Customer) => {
    const vatState = getVatState(customer);

    if (vatState === 'unchecked') {
      return <span className="customers-vat-indicator customers-vat-indicator--unchecked">{EMPTY_VALUE}</span>;
    }

    const valid = vatState === 'valid';
    const tooltipTitle = t(getVatValidationTranslation({
      valid,
      countryCode: customer.vatCountryCode
    }).key);

    return (
      <Tooltip title={tooltipTitle}>
        <span
          className={`customers-vat-indicator ${
            valid ? 'customers-vat-indicator--valid' : 'customers-vat-indicator--invalid'
          }`}
        >
          {valid ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
        </span>
      </Tooltip>
    );
  };

  const getRowMenuItems = (customer: Customer): MenuProps['items'] => {
    if (customer.deletedAt) {
      return [];
    }

    const items: NonNullable<MenuProps['items']> = [];

    if (canEditCustomers) {
      items.push({
        key: 'edit',
        icon: <EditOutlined />,
        label: t('customers:menu.edit')
      });
    }

    if (canDeleteCustomers) {
      items.push({
        key: 'delete',
        icon: <DeleteOutlined />,
        label: t('customers:menu.delete'),
        danger: true
      });
    }

    return items;
  };

  const bulkActionsEnabled = customerView === 'active' && canDeleteCustomers;

  const selectedCustomers = useMemo(
    () => filteredAndSortedCustomers.filter((customer) => selectedCustomerIds.includes(customer.id)),
    [filteredAndSortedCustomers, selectedCustomerIds]
  );

  useEffect(() => {
    setSelectedCustomerIds((current) =>
      current.filter((id) => filteredAndSortedCustomers.some((customer) => customer.id === id))
    );
  }, [filteredAndSortedCustomers]);

  const clearSelection = () => {
    setSelectedCustomerIds([]);
  };

  const handleBulkDelete = async () => {
    if (selectedCustomerIds.length === 0) {
      return;
    }

    const confirmed = await new Promise<boolean>((resolve) => {
      modal.confirm({
        title: t('customers:bulk.deleteConfirmTitle'),
        content: t('customers:bulk.deleteConfirmDescription'),
        okText: t('common:yes'),
        cancelText: t('common:no'),
        okButtonProps: { danger: true },
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });

    if (!confirmed) return;

    const results = await Promise.allSettled(
      selectedCustomerIds.map((customerId) => api.delete(`/customers/${customerId}`))
    );

    let deletedCount = 0;
    let archivedCount = 0;
    let failedCount = 0;

    results.forEach((result) => {
      if (result.status === 'fulfilled') {
        if (result.value.data?.archived) {
          archivedCount += 1;
        } else {
          deletedCount += 1;
        }
      } else {
        failedCount += 1;
      }
    });

    if (deletedCount > 0 || archivedCount > 0) {
      message.success(
        t('customers:bulk.deleteSuccess', {
          count: deletedCount + archivedCount,
          deletedCount,
          archivedCount
        })
      );
    }

    if (failedCount > 0) {
      message.error(t('customers:bulk.deletePartialError', { count: failedCount }));
    }

    clearSelection();
    fetchCustomers();
  };

  const rowSelection = bulkActionsEnabled
    ? {
        selectedRowKeys: selectedCustomerIds,
        onChange: (selectedRowKeys: React.Key[]) => setSelectedCustomerIds(selectedRowKeys as string[]),
        preserveSelectedRowKeys: false,
        columnWidth: 40,
        getCheckboxProps: (record: Customer) => ({
          disabled: Boolean(record.deletedAt)
        })
      }
    : undefined;

  const columns: TableColumnsType<Customer> = [
    {
      title: t('customers:table.name'),
      dataIndex: 'name',
      key: 'name',
      width: 165,
      render: (value: string, record: Customer) => (
        <Button
          className="customers-name-button"
          type="link"
          onClick={(event) => {
            event.stopPropagation();
            navigate(`/app/customers/${record.id}`);
          }}
        >
          <span className="customers-name-cell">
            <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <Tooltip title={value}>
                  <span className="customers-name-text">{value}</span>
                </Tooltip>
                {record.isBranch && (
                  <Tag bordered={false}>{t('customers:preview.branch')}</Tag>
                )}
              </span>
              {record.parentCustomerId && getCustomerParentName(record, customerNamesById) && (
                <Text type="secondary">
                  {t('customers:preview.parentCustomer')}: {getCustomerParentName(record, customerNamesById)}
                </Text>
              )}
            </span>
            {record.deletedAt && (
              <Tag className="customers-archived-tag" bordered={false}>
                {t('customers:views.archivedShort')}
              </Tag>
            )}
          </span>
        </Button>
      )
    },
    {
      title: t('customers:table.address'),
      dataIndex: 'address',
      key: 'address',
      width: 170,
      ellipsis: true,
      render: (value: string | null | undefined) =>
        value ? (
          <Tooltip title={value}>
            <span className="customers-address-text">{value}</span>
          </Tooltip>
        ) : (
          <Text type="secondary">{EMPTY_VALUE}</Text>
        )
    },
    {
      title: t('customers:table.contactPerson'),
      dataIndex: 'contactPerson',
      key: 'contactPerson',
      width: 150,
      render: (_value: string | null | undefined, record: Customer) => (
        <CustomerContactCell
          contacts={getCustomerContacts(record)}
          contactPerson={record.contactPerson}
          phone={record.phone}
          emptyValue={EMPTY_VALUE}
        />
      )
    },
    {
      title: t('customers:table.taxId'),
      key: 'taxId',
      width: 140,
      ellipsis: true,
      render: (_value: unknown, record: Customer) => {
        if (!record.taxId) {
          return <Text type="secondary">{EMPTY_VALUE}</Text>;
        }

        return (
          <Tooltip title={record.taxId}>
            <Space size={6}>
              {renderVatStatusIcon(record)}
              <span>{record.taxId}</span>
            </Space>
          </Tooltip>
        );
      }
    },
    {
      title: t('customers:table.email'),
      dataIndex: 'email',
      key: 'email',
      width: 155,
      ellipsis: true,
      render: (value: string | null | undefined) =>
        value ? (
          <Tooltip title={value}>
            <span>{value}</span>
          </Tooltip>
        ) : (
          <Text type="secondary">{EMPTY_VALUE}</Text>
        )
    }
  ];

  if (customerView === 'archived') {
    columns.push({
      title: t('customers:table.archivedAt'),
      dataIndex: 'deletedAt',
      key: 'deletedAt',
      width: 100,
      render: (value: string | undefined) => formatDateTime(value)
    });
  }

  if (canEditCustomers || canDeleteCustomers) {
    columns.push({
      title: '',
      key: 'menu',
      width: 40,
      align: 'right',
      render: (_value: unknown, record: Customer) => {
        if (record.deletedAt) {
          return null;
        }

        return (
          <Dropdown
            trigger={['click']}
            menu={{
              items: getRowMenuItems(record),
              onClick: ({ key, domEvent }) => {
                domEvent.stopPropagation();

                if (key === 'edit') {
                  navigate(`/app/customers/${record.id}`);
                }

                if (key === 'delete') {
                  setCustomerPendingDelete(record);
                }
              }
            }}
          >
            <Button
              className="customers-row-menu-button"
              type="text"
              icon={<MoreOutlined />}
              onClick={(event) => event.stopPropagation()}
              aria-label={t('customers:menu.open')}
            />
          </Dropdown>
        );
      }
    });
  }

  return (
    <div className="customers-page">
      <div className="customers-page__header">
        {!isTester && (
          <div className="customers-page__view-chips" role="tablist" aria-label={t('customers:views.label')}>
            <button
              type="button"
              role="tab"
              aria-selected={customerView === 'active'}
              className={`customers-page__view-chip ${
                customerView === 'active' ? 'customers-page__view-chip--active' : ''
              }`}
              onClick={() => setCustomerView('active')}
            >
              <span>{t('customers:views.active')}</span>
              <strong>{activeCustomersCount}</strong>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={customerView === 'archived'}
              className={`customers-page__view-chip ${
                customerView === 'archived' ? 'customers-page__view-chip--active' : ''
              }`}
              onClick={() => setCustomerView('archived')}
            >
              <span>{t('customers:views.archived')}</span>
              <strong>{archivedCustomersCount}</strong>
            </button>
          </div>
        )}

        {canCreateCustomers && (
          <Button
            className="customers-page__create-button"
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreateModal}
          >
            {t('customers:newCustomer')}
          </Button>
        )}
      </div>

      {!isTester && (
        <div className="customers-page__toolbar">
          <Input
            className="customers-page__quick-search"
            placeholder={t('customers:filters.search')}
            prefix={<SearchOutlined />}
            value={filters.search}
            onChange={(event) =>
              setFilters((current) => ({ ...current, search: event.target.value }))
            }
            allowClear
          />

          <Button
            className={`customers-page__filter-button ${
              filtersOpen ? 'customers-page__filter-button--open' : ''
            }`}
            icon={<FilterOutlined />}
            onClick={() => setFiltersOpen((current) => !current)}
          >
            {t('customers:filters.title')}
            {activeFilterCount > 0 && (
              <span className="customers-page__filter-count">{activeFilterCount}</span>
            )}
          </Button>

          <span className="customers-page__count">
            {t(
              customerView === 'active'
                ? 'customers:resultsCountActive'
                : 'customers:resultsCountArchived',
              { count: filteredAndSortedCustomers.length }
            )}
          </span>

          <Button
            className="customers-page__export-button"
            icon={<DownloadOutlined />}
            onClick={handleExportCsv}
          >
            {t('customers:exportCsv')}
          </Button>
        </div>
      )}

      {isTester && (
        <Card className="customers-tester-card" style={{ textAlign: 'center', padding: 40 }}>
          <p style={{ color: '#999', fontSize: 16 }}>
            {t('customers:testerMode')}
          </p>
        </Card>
      )}

      {!isTester && (
        <>
          <div className="customers-page__filter-summary">
            <button
              type="button"
              className="customers-page__filter-summary-button"
              onClick={() => setFiltersOpen((current) => !current)}
            >
              <FilterOutlined />
              <span>
                {activeFilterCount > 0
                  ? t('customers:filters.activeSummary', { count: activeFilterCount })
                  : t('customers:filters.emptySummary')}
              </span>
            </button>
            {activeFilterCount > 0 && (
              <Button size="small" type="text" onClick={() => setFilters(DEFAULT_FILTERS)}>
                {t('common:clear')}
              </Button>
            )}
          </div>

          {filtersOpen && (
            <div className="customers-filters">
              <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} lg={6}>
                      <div className="customers-filter-field customers-filter-field--sort">
                        <Text className="customers-filter-field__label">
                          {t('customers:filters.sortFieldLabel')}
                        </Text>
                      <Select
                        style={{ width: '100%' }}
                        value={sortField}
                        onChange={setSortField}
                        placeholder={t('customers:filters.sortField')}
                      >
                        <Select.Option value="name">{t('customers:sortFields.name')}</Select.Option>
                        <Select.Option value="phone">{t('customers:sortFields.phone')}</Select.Option>
                        <Select.Option value="taxId">{t('customers:sortFields.taxId')}</Select.Option>
                        <Select.Option value="regon">{t('customers:sortFields.regon')}</Select.Option>
                        <Select.Option value="address">{t('customers:sortFields.address')}</Select.Option>
                        <Select.Option value="vatStatus">{t('customers:sortFields.vatStatus')}</Select.Option>
                      </Select>
                      </div>
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <div className="customers-filter-field customers-filter-field--sort">
                        <Text className="customers-filter-field__label">
                          {t('customers:filters.sortDirectionLabel')}
                        </Text>
                      <Select
                        style={{ width: '100%' }}
                        value={sortDirection}
                        onChange={setSortDirection}
                        suffixIcon={
                          sortDirection === 'asc' ? <SortAscendingOutlined /> : <SortDescendingOutlined />
                        }
                        placeholder={t('customers:filters.sortDirection')}
                      >
                        <Select.Option value="asc">{t('customers:sortDirectionAsc')}</Select.Option>
                        <Select.Option value="desc">{t('customers:sortDirectionDesc')}</Select.Option>
                      </Select>
                      </div>
                    </Col>

                    <Col xs={24} sm={12} lg={8}>
                      <div className="customers-filter-field">
                        <Text className="customers-filter-field__label">
                          {t('customers:filters.nameLabel')}
                        </Text>
                      <Input
                        placeholder={t('customers:filters.name')}
                        value={filters.name}
                        onChange={(event) =>
                          setFilters((current) => ({ ...current, name: event.target.value }))
                        }
                        allowClear
                      />
                      </div>
                    </Col>
                    <Col xs={24} sm={12} lg={8}>
                      <div className="customers-filter-field">
                        <Text className="customers-filter-field__label">
                          {t('customers:filters.phoneLabel')}
                        </Text>
                      <Input
                        placeholder={t('customers:filters.phone')}
                        value={filters.phone}
                        onChange={(event) =>
                          setFilters((current) => ({ ...current, phone: event.target.value }))
                        }
                        allowClear
                      />
                      </div>
                    </Col>
                    <Col xs={24} sm={12} lg={8}>
                      <div className="customers-filter-field">
                        <Text className="customers-filter-field__label">
                          {t('customers:filters.vatStatusLabel')}
                        </Text>
                      <Select
                        style={{ width: '100%' }}
                        value={filters.vatStatus}
                        onChange={(value) =>
                          setFilters((current) => ({ ...current, vatStatus: value }))
                        }
                      >
                        <Select.Option value="all">{t('customers:filters.vatStatusAll')}</Select.Option>
                        <Select.Option value="valid">{t('customers:filters.vatStatusValid')}</Select.Option>
                        <Select.Option value="invalid">{t('customers:filters.vatStatusInvalid')}</Select.Option>
                        <Select.Option value="unchecked">{t('customers:filters.vatStatusUnchecked')}</Select.Option>
                      </Select>
                      </div>
                    </Col>

                    <Col xs={24} sm={12} lg={8}>
                      <div className="customers-filter-field">
                        <Text className="customers-filter-field__label">
                          {t('customers:filters.taxIdLabel')}
                        </Text>
                      <Input
                        placeholder={t('customers:filters.taxId')}
                        value={filters.taxId}
                        onChange={(event) =>
                          setFilters((current) => ({ ...current, taxId: event.target.value }))
                        }
                        allowClear
                      />
                      </div>
                    </Col>
                    <Col xs={24} sm={12} lg={8}>
                      <div className="customers-filter-field">
                        <Text className="customers-filter-field__label">
                          {t('customers:filters.regonLabel')}
                        </Text>
                      <Input
                        placeholder={t('customers:filters.regon')}
                        value={filters.regon}
                        onChange={(event) =>
                          setFilters((current) => ({ ...current, regon: event.target.value }))
                        }
                        allowClear
                      />
                      </div>
                    </Col>
                    <Col xs={24} sm={24} lg={8}>
                      <div className="customers-filter-field">
                        <Text className="customers-filter-field__label">
                          {t('customers:filters.addressLabel')}
                        </Text>
                      <Input
                        placeholder={t('customers:filters.address')}
                        value={filters.address}
                        onChange={(event) =>
                          setFilters((current) => ({ ...current, address: event.target.value }))
                        }
                        allowClear
                      />
                      </div>
                    </Col>

                    <Col span={24} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button onClick={() => setFilters(DEFAULT_FILTERS)}>
                        {t('common:clear')}
                      </Button>
                    </Col>
              </Row>
            </div>
          )}

          {customerView === 'archived' && (
            <div className="customers-page__archived-note">
              <Text type="secondary">{t('customers:archivedNote')}</Text>
            </div>
          )}

          {bulkActionsEnabled && (
            <div className="customers-bulk-toolbar">
              <div className="customers-bulk-toolbar__copy">
                <span className="customers-bulk-toolbar__label">{t('customers:bulk.label')}</span>
                <span className="customers-bulk-toolbar__count">
                  {selectedCustomerIds.length > 0
                    ? t('customers:bulk.selectedCount', { count: selectedCustomers.length })
                    : t('customers:bulk.empty')}
                </span>
              </div>

              <div className="customers-bulk-toolbar__actions">
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handleBulkDelete}
                  disabled={selectedCustomerIds.length === 0}
                >
                  {t('customers:bulk.delete')}
                </Button>

                <Button onClick={clearSelection} disabled={selectedCustomerIds.length === 0}>
                  {t('customers:bulk.clearSelection')}
                </Button>
              </div>
            </div>
          )}

          <Table<Customer>
            className="customers-table"
            columns={columns}
            dataSource={filteredAndSortedCustomers}
            rowKey="id"
            rowSelection={rowSelection}
            loading={loading}
            tableLayout="fixed"
            scroll={{ x: 920 }}
            rowClassName={(record) =>
              `customers-table__row${record.deletedAt ? ' customers-table__row--archived' : ''}`
            }
            pagination={{
              showSizeChanger: false
            }}
            locale={{
              emptyText:
                activeFilterCount > 0
                  ? customerView === 'active'
                    ? t('customers:noCustomersFound')
                    : t('customers:noArchivedCustomersFound')
                  : customerView === 'active'
                    ? t('customers:noCustomers')
                    : t('customers:noArchivedCustomers')
            }}
          />
        </>
      )}

      <Modal
        title={t('customers:confirmDeleteCustomer')}
        open={Boolean(customerPendingDelete)}
        onCancel={() => {
          if (!deletingCustomerId) {
            setCustomerPendingDelete(null);
          }
        }}
        onOk={() => customerPendingDelete && handleDeleteCustomer(customerPendingDelete.id)}
        okText={t('common:yes')}
        cancelText={t('common:no')}
        okButtonProps={{ danger: true }}
        confirmLoading={Boolean(deletingCustomerId)}
      >
        <p>{t('customers:confirmDeleteDescription')}</p>
      </Modal>

      <Modal
        title={t('customers:createCustomer')}
        open={modalVisible}
        onCancel={closeCustomerModal}
        maskClosable={false}
        footer={null}
        width={600}
        destroyOnClose
      >
        <CustomerForm
          form={form}
          customers={customers}
          editingCustomer={null}
          canViewCustomerPayments={canViewCustomerPayments}
          submitting={submitting}
          submitLabel={`${t('customers:form.create')} ${t('customers:customer')}`}
          onSubmit={handleCreate}
          isTester={isTester}
          open={modalVisible}
        />
      </Modal>
    </div>
  );
};

export default Customers;
